/**
 * Drills home: a card per drill mode + recommended-drill hero at the top.
 * Tapping a mode card opens the per-mode selector (with "Surprise me" +
 * curated options), NOT a fresh chat — to match iOS UX where each mode
 * has its own surprise variant.
 *
 * When the user is over their cap, we keep all drill UI visible but
 * **lock** the cards instead of hiding them. Locked cards still preview
 * what the user is missing (the upsell); clicking one pops the upgrade
 * modal IN PLACE (via DrillLaunchLink's pre-empt) rather than routing to
 * /pricing or navigating into a drill the user can't run. A persistent
 * banner up top shows a live countdown until the cap resets (or, for the
 * free hard wall, the subscribe CTA) — the banner's button is the one
 * deliberate /pricing link.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  ChevronRight,
  ICON_STROKE,
  Lock,
  MODE_ICONS,
  NavIcons,
} from "@/shared/components/icons";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { TodayPanel } from "@/shared/components/layout/TodayPanel";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { RecommendedDrillCard as RecommendedDrillCardInner } from "@/shared/components/RecommendedDrillCard";
import { TodaysDrillCard } from "@/features/daily/components/TodaysDrillCard";
import { FirstReportProgressCard } from "@/features/onboarding/components/FirstReportProgressCard";
import { fetchRecommendedDrill } from "@/shared/api";
import { getBillingStatus, isOverLimit } from "@/features/billing/lib/billing";
import {
  DRILL_MODES,
  SESSION_MODES,
  type SessionMode,
} from "@/shared/models";
import { useResetCountdown } from "@/shared/resetCountdown";

export function Drills() {
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
  });

  // Capped users see all drill UI in a "locked" presentation: the
  // card layout stays so they can see exactly what they're missing,
  // each card routes to /pricing on click, and a banner above shows
  // the countdown + upgrade CTA.
  const capped = !!(billing && isOverLimit(billing));
  const window = billing?.gatingWindow ?? "none";
  const hardWall = billing?.gateType === "hard_wall";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 flex flex-col lg:flex-row gap-8 items-start">
      <div className="flex-1 min-w-0 space-y-8 w-full">
        <PageHeader
          Icon={NavIcons.drills}
          title="What kind of session?"
          subtitle={
            capped
              ? hardWall
                ? "You've earned your first leak report — that's the free tier. Subscribe to keep drilling with the coach."
                : "You've hit your monthly cap. Drills are locked until reset, or unlock instantly with an upgrade."
              : "Pick a drill or just ask."
          }
        />

        {capped && <CappedBanner window={window} hardWall={hardWall} />}

        {/* Onboarding: persistent progress toward the first leak report.
            Self-hides for paid users + anyone past the 25-signal gate. */}
        <FirstReportProgressCard />

        {/* Today's Drill is cap-exempt — always shown, even when capped. */}
        <TodaysDrillCard />

        <RecommendedDrillCard locked={capped} />

        <section>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted/80 mb-3">
            Modes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DRILL_MODES.map((mode) => (
              <ModeCard key={mode} mode={mode} locked={capped} />
            ))}
          </div>
          <JustAskCard locked={capped} />
        </section>

        {capped && <StillFreeFooter />}
      </div>

      <TodayPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// CappedBanner — sits above the drill grid when capped. Compact, with a
// live countdown to next reset and an upgrade CTA. Doesn't hide the
// drill cards — those stay visible-but-locked to preserve the upsell.
// ---------------------------------------------------------------------------

function CappedBanner({
  window,
  hardWall,
}: {
  window: "daily" | "monthly" | "none";
  hardWall: boolean;
}) {
  // Hard wall has no reset → empty countdown anyway, but skip the hook's
  // window so it doesn't tick. Soft cap (paid) keeps the reset countdown.
  const countdown = useResetCountdown(hardWall ? "none" : window);
  const eyebrow = hardWall
    ? "Free tier complete"
    : window === "daily"
      ? "Daily cap reached"
      : "Monthly cap reached";

  return (
    <section className="rounded-2xl border border-accent/40 bg-surface p-5 shadow-[0_28px_60px_-30px_rgba(124,147,241,0.5)]">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1">
            {eyebrow}
          </div>
          {hardWall ? (
            <>
              <div className="text-base text-text">
                Subscribe to keep drilling with the coach.
              </div>
              <p className="text-xs text-text-muted mt-1">
                Your saved report, quizzes, and study sheets stay free.
              </p>
            </>
          ) : (
            <>
              <div className="text-base text-text">
                Drills unlock{" "}
                <span className="font-mono tabular-nums text-accent">
                  {countdown}
                </span>
              </div>
              <p className="text-xs text-text-muted mt-1">
                Or unlock everything instantly with an upgrade.
              </p>
            </>
          )}
        </div>
        <Link
          to="/pricing"
          className="self-start md:self-auto inline-flex px-4 py-3 rounded-xl bg-accent text-white text-sm font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
        >
          {hardWall ? "See plans" : "View pricing"}
        </Link>
      </div>
    </section>
  );
}

function StillFreeFooter() {
  return (
    <div className="text-xs text-text-muted/70 leading-relaxed">
      Quizzes, Study sheets, Skills + leak report, and Recent
      conversations stay free: open them from the side nav.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode cards — locked variant overlays a lock badge + sends clicks to
// /pricing instead of the drill selector. Card layout stays identical.
// ---------------------------------------------------------------------------

function JustAskCard({ locked }: { locked: boolean }) {
  return <ModeCard mode="just_ask" fullWidth className="mt-3" locked={locked} />;
}

function ModeCard({
  mode,
  fullWidth,
  className,
  locked = false,
}: {
  mode: SessionMode;
  fullWidth?: boolean;
  className?: string;
  locked?: boolean;
}) {
  const info = SESSION_MODES[mode];
  const Icon = MODE_ICONS[mode];
  // Always the real launch route — when locked, DrillLaunchLink pre-empts
  // the click and pops the upgrade modal in place (no /pricing redirect,
  // no navigating into a drill the user can't run).
  const to =
    mode === "just_ask" ? "/chat/new?mode=just_ask" : `/drills/${mode}`;
  return (
    <DrillLaunchLink
      to={to}
      title={locked ? "Subscribe to keep drilling" : undefined}
      className={[
        "block rounded-xl border p-5 transition group relative",
        locked
          ? "border-border/60 bg-surface/50 hover:border-accent/40"
          : "border-border bg-surface hover:border-accent/40 hover:bg-surface/80",
        fullWidth ? "" : "",
        className ?? "",
      ].join(" ")}
    >
      {locked && <LockBadge />}
      <div
        className={[
          "flex items-start gap-4",
          locked ? "opacity-60" : "",
        ].join(" ")}
      >
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center border border-accent/15">
          <Icon size={22} strokeWidth={ICON_STROKE} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={[
              "font-semibold tracking-tight transition",
              locked
                ? "text-text-muted"
                : "text-text group-hover:text-accent",
            ].join(" ")}
          >
            {info.title}
          </div>
          <div className="text-xs text-text-muted mt-1">{info.subtitle}</div>
          <div className="text-xs text-text-muted/70 mt-2 leading-relaxed">
            {info.description}
          </div>
        </div>
        {!locked && (
          <ChevronRight
            size={16}
            strokeWidth={ICON_STROKE}
            className="text-text-muted/60 group-hover:text-accent group-hover:translate-x-0.5 transition mt-1"
          />
        )}
      </div>
    </DrillLaunchLink>
  );
}

function LockBadge() {
  return (
    <div
      className="absolute top-3 right-3 z-10 pointer-events-none flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-accent/30 bg-accent/10 text-accent text-[10px] uppercase tracking-wider font-semibold"
      aria-hidden
    >
      <Lock size={11} strokeWidth={ICON_STROKE} />
      <span>Locked</span>
    </div>
  );
}

/** Local wrapper: fetches the recommendation and delegates rendering
 *  to the shared `RecommendedDrillCardInner` so the visual treatment
 *  matches the Skills page's version exactly. The Skills page already
 *  has the drill in hand from its own query, so it imports the inner
 *  component directly.
 *
 *  When locked, we render a dimmed wrapper around the same content +
 *  a lock badge + a pricing-bound click target. */
function RecommendedDrillCard({ locked }: { locked: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["recommended-drill"],
    queryFn: fetchRecommendedDrill,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-5 text-sm text-text-muted">
        Picking a drill for you…
      </div>
    );
  }
  if (error || !data) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted/80 mb-1">
          Recommended
        </div>
        <div className="text-sm text-text-muted">
          Not enough signal yet: pick a mode below and drill a few spots first.
        </div>
      </section>
    );
  }

  if (locked) {
    // Dim + badge, but let the inner card's DrillLaunchLink handle the
    // click — it pre-empts when capped and pops the upgrade modal in place
    // (no /pricing redirect). LockBadge is pointer-events-none so the whole
    // card stays clickable.
    return (
      <div className="relative rounded-2xl" title="Subscribe to keep drilling">
        <LockBadge />
        <div className="opacity-60">
          <RecommendedDrillCardInner drill={data} />
        </div>
      </div>
    );
  }

  return <RecommendedDrillCardInner drill={data} />;
}
